import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n";
import Logo from "@/components/site/Logo";

const Header = () => {
  const { t, lang, setLang, scrollTo } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "appartamento", label: t.nav.apartment, testid: "nav-apartment" },
    { id: "galleria", label: t.nav.gallery, testid: "nav-gallery" },
    { id: "posizione", label: t.nav.location, testid: "nav-location" },
    { id: "disponibilita", label: t.nav.calendar, testid: "nav-availability" },
    { id: "recensioni", label: t.nav.reviews, testid: "nav-reviews" },
    { id: "esperienze", label: t.nav.experiences, testid: "nav-experiences" },
  ];

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-[rgba(248,245,240,0.88)] backdrop-blur-xl shadow-[0_1px_0_rgba(13,37,58,0.08)]"
          : "bg-[rgba(248,245,240,0.55)] backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="text-[#0D253A]"
        >
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-[#0D253A]/80 hover:text-[#C86D51] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div data-testid="lang-switcher" className="flex items-center border border-[#0D253A]/15 rounded-full p-0.5 bg-white/60">
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1 text-xs font-semibold uppercase rounded-full transition-colors duration-300 ${
                  lang === l ? "bg-[#0D253A] text-[#F8F5F0]" : "text-[#0D253A]/60 hover:text-[#0D253A]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-book-cta"
            onClick={() => go("prenota")}
            className="hidden sm:inline-flex px-5 py-2.5 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] transition-colors duration-300"
          >
            {t.nav.book}
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-[#0D253A]"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[rgba(248,245,240,0.97)] backdrop-blur-xl border-t border-[#0D253A]/10 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-${l.testid}`}
              onClick={() => go(l.id)}
              className="text-left text-base font-medium text-[#0D253A] hover:text-[#C86D51] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-nav-book-cta"
            onClick={() => go("prenota")}
            className="mt-2 px-5 py-3 bg-[#C86D51] text-white text-sm font-semibold rounded-full"
          >
            {t.nav.book}
          </button>
        </div>
      )}
    </motion.header>
  );
};

export default Header;
